import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Fieldset } from "primereact/fieldset";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Row } from "primereact/row";

import { ToastService } from "../../../AppToast";
import { FormatDisplay } from "../../../shared/format";
import { generateId } from "../../../utils/generateId";
import {
  exceedsInvoiceListSelectionLimit,
  MAX_INVOICES_PER_INVOICE_LIST,
} from "../../../utils/invoiceListSelectionLimit";
import {
  calculateInvoiceListItemDiff,
  recalculateInvoiceListItem,
} from "../../../utils/invoiceListItemCalculation";
import { InvoiceService } from "../../hoa-don/InvoiceService";
import { InvoiceListService } from "../InvoiceListService";
import { DialogSelectInvoice } from "./DialogSelectInvoice";

interface Props {
  display?: boolean;
  setDisplay?: any;
  invoiceListId?: any;
  setSelectedInvoiceListId?: any;
  onSubmit?: any;
  selectedInvoices?: any[];
  invoiceType?: any;
  registerInvoices?: any[];
  viewOnly?: boolean;
}

const required = <span className="required">*</span>;

const invoiceListItemsSorting = {
  Sorting: "invoiceNumber",
  SortType: "DESCEND" as const,
};

const invoiceListItemColumns = [
  { field: "invoiceSerial", type: "text", isDisabled: true },
  { field: "invoiceNumber", type: "number", isDisabled: true },
  { field: "originalName", type: "text", isDisabled: false },
  { field: "originalQuantity", type: "number", isDisabled: false },
  { field: "originalUnitPrice", type: "number", isDisabled: false },
  { field: "originalAmountBeforeTax", type: "number", isDisabled: false },
  { field: "originalTaxRate", type: "dropdown", isDisabled: false },
  { field: "originalTax", type: "number", isDisabled: false },
  { field: "originalDiscountAmount", type: "number", isDisabled: false },
  { field: "originalAmountAfterTax", type: "number", isDisabled: false },
  { field: "updatedName", type: "text"},
  { field: "updatedQuantity", type: "number" },
  { field: "updatedUnitPrice", type: "number" },
  { field: "updatedAmountBeforeTax", type: "number" },
  { field: "updatedTaxRate", type: "dropdown" },
  { field: "updatedTax", type: "number" },
  { field: "updatedDiscountAmount", type: "number" },
  { field: "updatedAmountAfterTax", type: "number" },
  { field: "beforeTaxAmountDiff", type: "number", isDisabled: true },
  { field: "taxAmountDiff", type: "number", isDisabled: true },
  { field: "discountAmountDiff", type: "number", isDisabled: true },
  { field: "amountAfterTaxDiff", type: "number", isDisabled: true },
];

const initInvoiceList = {
  invoiceListNumber: "",
  invoiceListDate: new Date(),
  invoiceType: "",
  vendor: "",
  vendorTaxCode: "",
  vendorAddress: "",
  buyerLegalName: "",
  buyer: "",
  buyerTaxCode: "",
  buyerAddress: "",
  buyerCode: "",
  buyerEmail: "",
  buyerBankAccount: "",
  buyerBankName: "",
  buyerTel: "",
  buyerIdentityCard: "",
  passportNumber: "",
  buyerBudgetUnitCode: "",
  reasons: "",
  invoiceListItems: [],
};

const taxRates = [
  { value: "0%", label: "0%" },
  { value: "5%", label: "5%" },
  { value: "8%", label: "8%" },
  { value: "10%", label: "10%" },
  { value: "KCT", label: "KCT" },
  { value: "KKKNT", label: "KKKNT" },
  { value: "KHAC", label: "KHAC" },
];

const invoiceTypes = [
  { value: 1, translationKey: "common.multipleReplace" },
  { value: 2, translationKey: "common.multipleAdjust" },
];

const buyerExtensionFields = [
  "buyerCode",
  "buyerEmail",
  "buyerBankAccount",
  "buyerBankName",
  "buyerTel",
  "buyerIdentityCard",
  "passportNumber",
  "buyerBudgetUnitCode",
];

const beforeAdjustmentHeaderStyle = {
  backgroundColor: "#3D85C6",
  color: "#ffffff",
  justifyContent: "center",
};

const afterAdjustmentHeaderStyle = {
  backgroundColor: "#FCE5CD",
  color: "#333333",
  justifyContent: "center",
};

const diffHeaderStyle = {
  backgroundColor: "#D9EAD3",
  color: "#333333",
  justifyContent: "center",
};

const beforeAdjustmentHeaders = [
  "common.productServiceName",
  "common.quantity",
  "common.unitPrice",
  "report.amountBeforeTax",
  "common.vatRate",
  "common.taxAmount",
  "common.otherAmount",
  "common.totalAfterTax",
];

const afterAdjustmentHeaders = [...beforeAdjustmentHeaders];

const diffHeaders = [
  "report.amountBeforeTax",
  "common.taxAmount",
  "common.otherAmount",
  "common.totalAfterTax",
];

const getFirstValue = (items, fields) => {
  for (const item of items || []) {
    for (const field of fields) {
      if (item?.[field]) {
        return item[field];
      }
    }
  }
  return "";
};

const getSellerInfo = (items) => ({
  vendor: getFirstValue(items, ["vendor", "sellerLegalName"]),
  vendorTaxCode: getFirstValue(items, ["vendorTaxCode", "sellerTaxCode"]),
  vendorAddress: getFirstValue(items, ["vendorAddress", "sellerAddress"]),
});

const getBuyerInfo = (items) => ({
  buyerLegalName: getFirstValue(items, ["buyerLegalName"]),
  buyer: getFirstValue(items, ["buyer", "buyerDisplayName", "buyerName"]),
  buyerTaxCode: getFirstValue(items, ["buyerTaxCode"]),
  buyerAddress: getFirstValue(items, ["buyerAddress"]),
  buyerCode: getFirstValue(items, ["buyerCode"]),
  buyerEmail: getFirstValue(items, ["buyerEmail"]),
  buyerBankAccount: getFirstValue(items, ["buyerBankAccount"]),
  buyerBankName: getFirstValue(items, ["buyerBankName"]),
  buyerTel: getFirstValue(items, ["buyerTel"]),
  buyerIdentityCard: getFirstValue(items, ["buyerIdentityCard"]),
  passportNumber: getFirstValue(items, ["passportNumber"]),
  buyerBudgetUnitCode: getFirstValue(items, ["buyerBudgetUnitCode"]),
});

const getInvoiceListItemKey = (item) => {
  if (item.originalInvoiceId) {
    return `invoice-id-${item.originalInvoiceId}`;
  }
  if (item.invoiceSerial || item.invoiceNumber || item.originalName) {
    return `invoice-${item.invoiceSerial || ""}-${item.invoiceNumber || ""}-${
      item.originalName || ""
    }`;
  }
  return `row-${item.id}`;
};

const requiredInvoiceListFields = [
  { field: "invoiceListDate", translationKey: "common.invoiceListDate" },
  { field: "invoiceType", translationKey: "common.invoiceNature" },
  { field: "reasons", translationKey: "common.errorReason" },
];

const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && !value.trim());

const DialogBangKe = ({
  display,
  setDisplay,
  invoiceType = 1,
  registerInvoices = [],
  invoiceListId = null,
  selectedInvoices = [],
  setSelectedInvoiceListId = () => {},
  onSubmit = () => {},
  viewOnly = false,
}: Props) => {
  const { t } = useTranslation();
  const [invoiceListItems, setInvoiceListItems] = React.useState([]);
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [first, setFirst] = React.useState(0);
  const [rows, setRows] = React.useState(50);
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [loadingItems, setLoadingItems] = React.useState(false);
  const [serverPagination, setServerPagination] = React.useState(false);
  const [displaySelectInvoice, setDisplaySelectInvoice] =
    React.useState(false);
  const pendingDropdownValuesRef = React.useRef(new Map<string, any>());
  const dialogSessionRef = React.useRef(0);
  const invoiceListItemsRequestRef = React.useRef(0);
  const loadAllItemsRequestRef = React.useRef<Promise<any[]> | null>(null);
  const editedInvoiceListItemsRef = React.useRef(new Map<any, any>());
  const [invoiceListForm, setInvoiceListForm] = React.useState({
    ...initInvoiceList,
    invoiceListDate: new Date(),
    invoiceType,
  });

  const getInitInvoiceList = () => ({
    ...initInvoiceList,
    invoiceListDate: new Date(),
    invoiceType,
  });

  const normalizeItemsResult = (result) => ({
    items: Array.isArray(result) ? result : result?.items || [],
    totalCount: Array.isArray(result)
      ? result.length
      : result?.totalCount ?? result?.items?.length ?? 0,
  });

  const sortInvoiceListItems = (items) =>
    [...(items || [])].sort((firstItem, secondItem) => {
      const firstInvoiceNumber = Number(firstItem?.invoiceNumber);
      const secondInvoiceNumber = Number(secondItem?.invoiceNumber);
      const safeFirstInvoiceNumber = Number.isFinite(firstInvoiceNumber)
        ? firstInvoiceNumber
        : Number.NEGATIVE_INFINITY;
      const safeSecondInvoiceNumber = Number.isFinite(secondInvoiceNumber)
        ? secondInvoiceNumber
        : Number.NEGATIVE_INFINITY;

      return safeSecondInvoiceNumber - safeFirstInvoiceNumber;
    });

  const mapInvoiceListItems = (items) =>
    sortInvoiceListItems(
      (items || []).map(calculateInvoiceListItemDiff),
    );

  const loadInvoiceListItemPage = (id, skipCount, maxResultCount) => {
    const dialogSession = dialogSessionRef.current;
    const request = ++invoiceListItemsRequestRef.current;
    setLoadingItems(true);

    return InvoiceListService.getItems(id, {
      SkipCount: skipCount,
      MaxResultCount: maxResultCount,
      ...invoiceListItemsSorting,
    })
      .then((result) => {
        if (
          dialogSession !== dialogSessionRef.current ||
          request !== invoiceListItemsRequestRef.current
        ) {
          return;
        }
        const pagedResult = normalizeItemsResult(result);
        setInvoiceListItems(
          mapInvoiceListItems(pagedResult.items).map(
            (item) => editedInvoiceListItemsRef.current.get(item.id) || item,
          ),
        );
        setTotalRecords(pagedResult.totalCount);
        setSelectedItems([]);
      })
      .catch(() => {
        if (
          dialogSession === dialogSessionRef.current &&
          request === invoiceListItemsRequestRef.current
        ) {
          ToastService.error(t("common.invoiceListItemsLoadFailed"));
        }
      })
      .finally(() => {
        if (
          dialogSession === dialogSessionRef.current &&
          request === invoiceListItemsRequestRef.current
        ) {
          setLoadingItems(false);
        }
      });
  };

  const enableLocalEditing = (currentPageItems = invoiceListItems) => {
    if (!invoiceListId || !serverPagination) {
      return Promise.resolve(currentPageItems);
    }
    if (loadAllItemsRequestRef.current) {
      return loadAllItemsRequestRef.current;
    }

    const dialogSession = dialogSessionRef.current;
    invoiceListItemsRequestRef.current += 1;
    setLoadingItems(true);
    loadAllItemsRequestRef.current = InvoiceListService.getItems(invoiceListId, {
      SkipCount: 0,
      MaxResultCount: MAX_INVOICES_PER_INVOICE_LIST,
      ...invoiceListItemsSorting,
    })
      .then((result) => {
        if (dialogSession !== dialogSessionRef.current) {
          return [];
        }
        const pagedResult = normalizeItemsResult(result);
        const currentItemsById = new Map([
          ...Array.from(editedInvoiceListItemsRef.current.entries()),
          ...currentPageItems.map((item) => [item.id, item] as [any, any]),
        ]);
        const allItems = mapInvoiceListItems(pagedResult.items).map(
          (item) => currentItemsById.get(item.id) || item,
        );

        editedInvoiceListItemsRef.current.clear();
        setInvoiceListItems(allItems);
        setTotalRecords(allItems.length);
        setServerPagination(false);
        return allItems;
      })
      .catch((error) => {
        if (dialogSession === dialogSessionRef.current) {
          ToastService.error(t("common.invoiceListItemsLoadFailed"));
        }
        throw error;
      })
      .finally(() => {
        if (dialogSession === dialogSessionRef.current) {
          setLoadingItems(false);
        }
        loadAllItemsRequestRef.current = null;
      });

    return loadAllItemsRequestRef.current;
  };

  const onFieldChange = (e) => {
    const { name, value } = e.target;
    onFieldValueChange(name, value);
  };

  const onFieldValueChange = (name, value) => {
    setInvoiceListForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const onEditorKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== "Tab") {
      e.stopPropagation();
    }
  };

  const getCellEditKey = (rowData, rowIndex, field) =>
    `${rowData?.id ?? rowIndex}-${field}`;

  const updateInvoiceListItemValue = (rowData, field, value) => {
    const updateItems = (items) =>
      items.map((item) =>
        item.id === rowData.id
          ? recalculateInvoiceListItem(
              { ...item, [field]: value },
              field,
            )
          : item,
      );
    const updatedCurrentPage = updateItems(invoiceListItems);
    setInvoiceListItems(updatedCurrentPage);
    if (serverPagination) {
      const updatedItem = updatedCurrentPage.find(
        (item) => item.id === rowData.id,
      );
      if (updatedItem) {
        editedInvoiceListItemsRef.current.set(updatedItem.id, updatedItem);
      }
    }
  };

  const onDropdownValueChange = (options, value) => {
    pendingDropdownValuesRef.current.set(
      getCellEditKey(options.rowData, options.rowIndex, options.field),
      value,
    );
    options.editorCallback(value);
    updateInvoiceListItemValue(options.rowData, options.field, value);
  };

  const textEditor = (options) => (
    <InputText
      style={{ width: "148px" }}
      type="text"
      value={options.value}
      onChange={(e) => options.editorCallback(e.target.value)}
      onKeyDown={onEditorKeyDown}
    />
  );

  const numberEditor = (options) => (
    <InputNumber
      locale={FormatDisplay.locales}
      inputStyle={{ width: "148px", textAlign: "right" }}
      value={options.value}
      onValueChange={(e) => options.editorCallback(e.value)}
      onKeyDown={onEditorKeyDown}
    />
  );

  const dropdownEditor = (options) => (
    <Dropdown
      style={{ width: "148px" }}
      value={options.value}
      options={taxRates}
      optionLabel="label"
      optionValue="value"
      onChange={(e) => onDropdownValueChange(options, e.value)}
    />
  );

  const cellEditor = (options, type) => {
    if (type === "number") {
      return numberEditor(options);
    }
    if (type === "dropdown") {
      return dropdownEditor(options);
    }
    return textEditor(options);
  };

  const numberBody = (rowData, field) => FormatDisplay.number(rowData[field]);

  // Với bảng kê điều chỉnh, nhóm cột này ghi nhận thông tin đúng để làm căn cứ tính chênh lệch.
  const updatedInformationHeader =
    invoiceListForm.invoiceType === 1
      ? t("common.afterReplacementInfo")
      : t("common.afterAdjustmentInfo");

  const headerGroup = (
    <ColumnGroup>
      <Row>
        {!viewOnly && <Column rowSpan={2} style={{ width: "50px" }} />}
        <Column header={t("common.serialNumberShort")} rowSpan={2} style={{ width: "50px" }} />
        <Column header={t("common.invoiceSymbol")} rowSpan={2} style={{ width: "150px" }}  />
        <Column header={t("common.invoiceNumber")} rowSpan={2} style={{ width: "150px" }} />
        <Column
          header={t(
            invoiceListForm.invoiceType === 1
              ? "common.beforeReplacementInfo"
              : "common.beforeAdjustmentInfo",
          )}
          colSpan={8}
          style={{ ...beforeAdjustmentHeaderStyle, width: "1200px" }}
        />
        <Column
          header={updatedInformationHeader}
          colSpan={8}
          style={{ ...afterAdjustmentHeaderStyle, width: "1200px" }}
        />
        <Column
          header={t("common.difference")}
          colSpan={4}
          style={{ ...diffHeaderStyle, width: "600px" }}
        />
      </Row>
      <Row>
        {beforeAdjustmentHeaders.map((header) => (
          <Column key={`before-${header}`} header={t(header)} colSpan={1} style={beforeAdjustmentHeaderStyle} />
        ))}
        {afterAdjustmentHeaders.map((header) => (
          <Column key={`after-${header}`} header={t(header)} colSpan={1} style={afterAdjustmentHeaderStyle} />
        ))}
        {diffHeaders.map((header) => (
          <Column key={`diff-${header}`} header={t(header)} colSpan={1} style={diffHeaderStyle} />
        ))}
      </Row>
    </ColumnGroup>
  );

  const onCellEditComplete = (e) => {
    const { rowData, rowIndex, newValue, field } = e;
    const editKey = getCellEditKey(rowData, rowIndex, field);
    const committedValue = pendingDropdownValuesRef.current.has(editKey)
      ? pendingDropdownValuesRef.current.get(editKey)
      : newValue;
    pendingDropdownValuesRef.current.delete(editKey);
    updateInvoiceListItemValue(rowData, field, committedValue);
  };

  const onCellEditCancel = (e) => {
    pendingDropdownValuesRef.current.delete(
      getCellEditKey(e.rowData, e.rowIndex, e.field),
    );
  };

  const loadInvoiceListItems = (invoices, append = false) => {
    if (!invoices || invoices.length === 0) {
      return false;
    }
    if (
      exceedsInvoiceListSelectionLimit(
        append ? invoiceListItems : [],
        invoices,
      )
    ) {
      ToastService.error(
        t("common.invoiceListSelectionLimit", {
          count: MAX_INVOICES_PER_INVOICE_LIST,
        }),
      );
      return false;
    }
    const invoiceIds = Array.from(
      new Set(invoices.map((item) => item.id || item.invoiceId)),
    ).filter(Boolean);
    if (!invoiceIds.length) {
      return false;
    }
    const dialogSession = dialogSessionRef.current;
    const selectedInvoiceById = new Map<any, any>(
      invoices
        .map((item) => [item.id || item.invoiceId, item])
        .filter(([id]) => !!id),
    );
    InvoiceService.getInvoiceListItem(invoiceIds).then(async (res) => {
      if (dialogSession !== dialogSessionRef.current) {
        return;
      }
      const currentItems =
        append && serverPagination
          ? await enableLocalEditing(invoiceListItems)
          : invoiceListItems;
      if (dialogSession !== dialogSessionRef.current) {
        return;
      }
      const nextItems = (res || []).map((item) => {
        const invoiceId =
          item.originalInvoiceId ||
          item.invoiceId ||
          selectedInvoiceById.get(item.id)?.id ||
          selectedInvoiceById.get(item.invoiceId)?.id ||
          item.id;
        const selectedInvoice = selectedInvoiceById.get(invoiceId);

        return calculateInvoiceListItemDiff({
          ...item,
          originalInvoiceId: invoiceId,
          invoiceDate:
            item.invoiceDate ||
            item.originalInvoiceDate ||
            selectedInvoice?.invoiceDate,
          id: generateId(),
        });
      });
      const sellerInfo = getSellerInfo([...(invoices || []), ...nextItems]);
      const firstInvoice = invoices[0];
      const firstInvoiceId = firstInvoice?.id || firstInvoice?.invoiceId;
      const firstInvoiceDetails =
        nextItems.find(
          (item) => item.originalInvoiceId === firstInvoiceId,
        ) || nextItems[0];
      const buyerInfo = getBuyerInfo([firstInvoice, firstInvoiceDetails]);
      const shouldBindBuyer = !append || currentItems.length === 0;
      setInvoiceListForm((prevState) => ({
        ...prevState,
        vendor: prevState.vendor || sellerInfo.vendor,
        vendorTaxCode: prevState.vendorTaxCode || sellerInfo.vendorTaxCode,
        vendorAddress: prevState.vendorAddress || sellerInfo.vendorAddress,
        ...(shouldBindBuyer ? buyerInfo : {}),
      }));
      if (!append) {
        const sortedItems = sortInvoiceListItems(nextItems);
        setInvoiceListItems(sortedItems);
        setTotalRecords(nextItems.length);
        return;
      }
      const existingKeys = new Set(currentItems.map(getInvoiceListItemKey));
      const mergedItems = nextItems.filter(
        (item) => !existingKeys.has(getInvoiceListItemKey(item)),
      );
      const allItems = sortInvoiceListItems([
        ...currentItems,
        ...mergedItems,
      ]);
      setInvoiceListItems(allItems);
      setTotalRecords(allItems.length);
    }).catch(() => {
      ToastService.error(t("common.invoiceInfoLoadFailed"));
    });
    return true;
  };

  const addSelectedInvoices = (items) => {
    const invoices = Array.isArray(items) ? items : [items];
    return loadInvoiceListItems(invoices, true);
  };

  const deleteItem = () => {
    if (selectedItems.length) {
      const selectedIds = new Set(selectedItems.map((item) => item.id));
      enableLocalEditing(invoiceListItems)
        .then((items) => {
          const remainingItems = items.filter(
            (row) => !selectedIds.has(row.id),
          );
          setInvoiceListItems(remainingItems);
          setTotalRecords(remainingItems.length);
          setSelectedItems([]);
        })
        .catch(() => {});
    }
  };

  const deleteItemsWithoutDiff = () => {
    enableLocalEditing(invoiceListItems)
      .then((items) => {
        const remainingItems = items.filter(
          (item) =>
            item.beforeTaxAmountDiff !== 0 ||
            item.taxAmountDiff !== 0 ||
            item.discountAmountDiff !== 0 ||
            item.amountAfterTaxDiff !== 0,
        );
        setInvoiceListItems(remainingItems);
        setTotalRecords(remainingItems.length);
        setSelectedItems([]);
      })
      .catch(() => {});
  };

  const resetDialogState = () => {
    dialogSessionRef.current += 1;
    invoiceListItemsRequestRef.current += 1;
    loadAllItemsRequestRef.current = null;
    editedInvoiceListItemsRef.current.clear();
    pendingDropdownValuesRef.current.clear();
    setInvoiceListForm(getInitInvoiceList());
    setInvoiceListItems([]);
    setSelectedItems([]);
    setFirst(0);
    setTotalRecords(0);
    setLoadingItems(false);
    setServerPagination(false);
    setDisplaySelectInvoice(false);
  };

  useEffect(() => {
    if (serverPagination) {
      return;
    }
    const lastPageFirst = Math.max(
      0,
      Math.floor(Math.max(invoiceListItems.length - 1, 0) / rows) * rows,
    );
    if (first > lastPageFirst) {
      setFirst(lastPageFirst);
    }
  }, [first, invoiceListItems.length, rows, serverPagination]);

  const close = () => {
    resetDialogState();
    setSelectedInvoiceListId(null);
    setDisplay(false);
  };

  const buildPayload = (items = invoiceListItems) => {
    const payload = {
      ...invoiceListForm,
      invoiceListItems: items.map((item) => {
        const invoiceSerial = item.invoiceSerial || "";
        return {
          ...item,
          invoiceTemplateCode: invoiceSerial[0],
          invoiceCode: invoiceSerial.slice(1),
        };
      }),
    };
    buyerExtensionFields.forEach((field) => delete payload[field]);
    return payload;
  };

  const createInvoiceList = () => {
    const payload = buildPayload();
    return InvoiceListService.create(payload).then((res) => {
      ToastService.success(t("common.invoiceListCreated"));
      return {
        ...payload,
        ...res,
        ...getBuyerInfo([res, invoiceListForm]),
      };
    });
  };

  const updateInvoiceList = (id, items) =>
    InvoiceListService.update(id, buildPayload(items)).then(() => {
      ToastService.success(t("common.invoiceListUpdated"));
    });

  const validateRequiredFields = () => {
    const missingField = requiredInvoiceListFields.find(({ field }) =>
      isEmptyValue(invoiceListForm[field]),
    );

    if (missingField) {
      ToastService.error(
        t("validation.required", { field: t(missingField.translationKey) }),
      );
      return false;
    }

    if (
      isEmptyValue(invoiceListForm.buyerLegalName) &&
      isEmptyValue(invoiceListForm.buyer)
    ) {
      ToastService.error(t("common.buyerNameRequired"));
      return false;
    }

    if (!(serverPagination ? totalRecords : invoiceListItems.length)) {
      ToastService.error(t("common.invoiceListItemsRequired"));
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateRequiredFields()) {
      return;
    }

    if (invoiceListId) {
      let allItems;
      try {
        allItems = await enableLocalEditing(invoiceListItems);
      } catch {
        return;
      }
      await updateInvoiceList(invoiceListId, allItems);
      resetDialogState();
      onSubmit();
      return;
    }
    const result = await createInvoiceList();
    resetDialogState();
    onSubmit(result);
  };

  useEffect(() => {
    setInvoiceListForm((prevState) => ({
      ...prevState,
      invoiceType,
    }));
  }, [invoiceType]);

  useEffect(() => {
    if (!display) {
      resetDialogState();
    }
  }, [display]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (display && invoiceListId) {
      const dialogSession = dialogSessionRef.current;
      setFirst(0);
      setServerPagination(true);
      InvoiceListService.get(invoiceListId).then((res) => {
        if (dialogSession !== dialogSessionRef.current) {
          return;
        }
        if (res) {
          const invoiceListInfo = { ...res };
          delete invoiceListInfo.invoiceListItems;
          setInvoiceListForm({
            ...invoiceListInfo,
            invoiceListDate: new Date(res.invoiceListDate),
          });
        } else {
          ToastService.error(t("common.invoiceListLoadFailed"));
        }
      }).catch(() => {
        if (dialogSession === dialogSessionRef.current) {
          ToastService.error(t("common.invoiceListLoadFailed"));
        }
      });
      loadInvoiceListItemPage(invoiceListId, 0, rows);
    }
  }, [display, invoiceListId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (display && selectedInvoices.length > 0) {
      loadInvoiceListItems(selectedInvoices);
    }
  }, [display, selectedInvoices]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog
      header={viewOnly ? t("common.invoiceListViewTitle") : t("common.invoiceListTitle")}
      footer={() => (
        <div className="flex justify-content-center">
          <Button
            label={viewOnly ? t("common.close") : t("common.cancel")}
            className="p-button-outlined p-button-secondary p-button-sm mr-2"
            onClick={close}
          />
          {!viewOnly && (
            <Button
              label={t("common.saveAndCreateInvoice")}
              className="p-button-outlined p-button-sm mr-2"
              onClick={handleSubmit}
            />
          )}
        </div>
      )}
      visible={display}
      style={{ width: "100vw", height: "100vh" }}
      position="top"
      onHide={close}
      className="form-xs bg-theme crup-invoice"
    >
      <div className="grid p-1">
        <div className="col-3">
          <Fieldset legend={t("common.generalInfo")} className="h-full">
            <div className="p-fluid grid">
              <label className="col-12 mb-2 md:col-6 md:mb-0">
                {t("common.invoiceListSymbol")}{required}
              </label>
              <div className="col-12 md:col-6">
                <InputText disabled value="01/BK-ĐCTT" />
              </div>

              <label className="col-12 mb-2 md:col-6 md:mb-0">
                {t("common.invoiceListNo")}
              </label>
              <div className="col-12 md:col-6">
                <InputText
                  name="invoiceListNumber"
                  value={invoiceListForm.invoiceListNumber}
                  onChange={onFieldChange}
                  disabled
                />
              </div>

              <label className="col-12 mb-2 md:col-6 md:mb-0">
                {t("common.invoiceListDate")}{required}
              </label>
              <div className="col-12 md:col-6">
                <Calendar
                  name="invoiceListDate"
                  value={invoiceListForm.invoiceListDate}
                  onChange={onFieldChange}
                  dateFormat="dd/mm/yy"
                  disabled
                />
              </div>

              <label className="col-12 mb-2 md:col-6 md:mb-0">
                {t("common.invoiceNature")}{required}
              </label>
              <div className="col-12 md:col-6">
                <Dropdown
                  value={invoiceListForm.invoiceType}
                  onChange={(e) => onFieldValueChange("invoiceType", e.value)}
                  options={invoiceTypes.map((option) => ({
                    ...option,
                    name: t(option.translationKey),
                  }))}
                  optionLabel="name"
                  disabled
                />
              </div>
            </div>
          </Fieldset>
        </div>

        <div className="col-6">
          <Fieldset legend={t("common.buyerInfo")} className="h-full">
            <div className="p-fluid grid">
              <label className="col-12 mb-2 md:col-4 md:mb-0">
                {t("common.buyerLegalName")}
              </label>
              <div className="col-12 md:col-8">
                <InputText
                  name="buyerLegalName"
                  value={invoiceListForm.buyerLegalName}
                  onChange={onFieldChange}
                  disabled={viewOnly}
                />
              </div>

              <label className="col-12 mb-2 md:col-4 md:mb-0">
                {t("common.buyerDisplayName")}
              </label>
              <div className="col-12 md:col-8">
                <InputText
                  name="buyer"
                  value={invoiceListForm.buyer}
                  onChange={onFieldChange}
                  disabled={viewOnly}
                />
              </div>

              <label className="col-12 mb-2 md:col-4 md:mb-0">
                {t("common.buyerTaxCode")}
              </label>
              <div className="col-12 md:col-8">
                <InputText
                  name="buyerTaxCode"
                  value={invoiceListForm.buyerTaxCode}
                  onChange={onFieldChange}
                  disabled={viewOnly}
                />
              </div>

              <label className="col-12 mb-2 md:col-4 md:mb-0">
                {t("common.buyerAddress")}
              </label>
              <div className="col-12 md:col-8">
                <InputTextarea
                  name="buyerAddress"
                  value={invoiceListForm.buyerAddress}
                  onChange={onFieldChange}
                  rows={3}
                  disabled={viewOnly}
                />
              </div>
            </div>
          </Fieldset>
        </div>

        <div className="col-3">
          <Fieldset
            legend={
              <span>
                {t("common.errorReason")} <span className="text-red-500">*</span>
              </span>
            }
            className="h-full"
          >
            <div className="p-fluid grid">
              <div className="col-12 md:col-12">
                <InputTextarea
                  name="reasons"
                  value={invoiceListForm.reasons}
                  onChange={onFieldChange}
                  required
                  rows={9}
                  className="h-full"
                  disabled={viewOnly}
                />
              </div>
            </div>
          </Fieldset>
        </div>

        <div className="col-12 p-1">
          {!viewOnly && (
            <>
              <Button
                icon="pi pi-search"
                label={t("common.addInvoice")}
                className="p-button-outlined p-button-sm mr-2"
                onClick={() => {
                  enableLocalEditing(invoiceListItems)
                    .then(() => setDisplaySelectInvoice(true))
                    .catch(() => {});
                }}
              />
              <Button
                icon="pi pi-trash"
                label={t("common.deleteInvoiceRowWithShortcut")}
                className="p-button-outlined p-button-danger p-button-sm mr-2"
                onClick={deleteItem}
                disabled={!selectedItems.length}
              />
              <Button
                icon="pi pi-trash"
                label={t("common.deleteNoDifferenceRows")}
                className="p-button-outlined p-button-danger p-button-sm mr-2"
                onClick={deleteItemsWithoutDiff}
              />
            </>
          )}
          <DataTable
            className="invoice-list-editable-table"
            value={invoiceListItems}
            selection={viewOnly ? undefined : selectedItems}
            onSelectionChange={
              viewOnly ? undefined : (e) => setSelectedItems(e.value)
            }
            showGridlines
            size="small"
            scrollable
            paginator
            lazy={serverPagination}
            loading={loadingItems}
            totalRecords={
              serverPagination ? totalRecords : invoiceListItems.length
            }
            first={first}
            rows={rows}
            rowsPerPageOptions={[50, 100, 200, 300]}
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate={t("common.currentPageReport")}
            paginatorLeft={<div />}
            paginatorRight={<div />}
            onPage={(event) => {
              setFirst(event.first);
              setRows(event.rows);
              if (serverPagination && invoiceListId) {
                loadInvoiceListItemPage(
                  invoiceListId,
                  event.first,
                  event.rows,
                );
              }
            }}
            dataKey="id"
            filterDisplay="row"
            headerColumnGroup={headerGroup}
            selectionMode={viewOnly ? undefined : "checkbox"}
            emptyMessage={t("common.noData")}
            editMode={viewOnly ? undefined : "cell"}
          >
            {!viewOnly && (
              <Column
                frozen
                selectionMode="multiple"
                style={{
                  minWidth: "50px",
                  maxWidth: "50px",
                  justifyContent: "center",
                }}
              />
            )}
            <Column
              body={(item, row) => <>{row.rowIndex + 1}</>}
              style={{
                minWidth: "50px",
                maxWidth: "50px",
                justifyContent: "center",
              }}
            />
            {invoiceListItemColumns.map(
              ({ field, type, isDisabled = false }) => (
                <Column
                  style={{
                    background: "transparent",
                    minWidth: "150px",
                    maxWidth: "150px",
                    justifyContent: type === "number" ? "end" : "start",
                  }}
                  key={field}
                  field={field}
                  header={field}
                  body={
                    type === "number"
                      ? (rowData) => numberBody(rowData, field)
                      : undefined
                  }
                  editor={
                    !viewOnly && !isDisabled
                      ? (options) => cellEditor(options, type)
                      : undefined
                  }
                  onCellEditComplete={
                    !viewOnly && !isDisabled ? onCellEditComplete : undefined
                  }
                  onCellEditCancel={
                    !viewOnly && !isDisabled ? onCellEditCancel : undefined
                  }
                />
              ),
            )}
          </DataTable>
        </div>
      </div>
      {!viewOnly && (
        <DialogSelectInvoice
          display={displaySelectInvoice}
          setDisplay={setDisplaySelectInvoice}
          addSelectedItems={addSelectedInvoices}
          selectionMode="checkbox"
          invoiceStatus="0"
          sendTaxStatus="2,4"
          registerInvoices={registerInvoices}
          invoiceListMode={invoiceListForm.invoiceType}
          selectedInvoices={invoiceListItems.map((item) => ({
            ...item,
            buyerLegalName:
              item.buyerLegalName || invoiceListForm.buyerLegalName,
            buyerDisplayName:
              item.buyerDisplayName || invoiceListForm.buyer,
          }))}
        />
      )}
    </Dialog>
  );
};

export default DialogBangKe;
